import { useCallback, useEffect, useRef, useState } from 'react';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { NovaPessoaForm } from '@/components/Admin/NovaPessoaForm';
import { PessoaDialog } from '@/components/Admin/PessoaDialog';
import { UsuariosTable } from '@/components/Admin/UsuariosTable';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/Dialog';
import { mapAdminError } from '@/components/Admin/messages';
import { Button, Input, Select } from '@/components/ui';
import {
  AdminError,
  criarPessoa,
  listClientes,
  listEscritorios,
  listUsuarios,
  type AmbienteAdmin,
  type ClienteAdmin,
  type EscritorioAdmin,
  type EstadoAdmin,
  type UsuarioAdmin,
} from '@/lib/plataforma_admin';

const _POR_PAGINA = 20;

function _mensagem(err: unknown): string {
  return err instanceof AdminError ? mapAdminError(err.reason) : ptBR.admin.erroGenerico;
}

/**
 * Console `/admin/usuarios` — pessoas e acessos (E53.F6, ADR-0064 §4). // ux-neutrality:allow doc-comment
 *
 * A tela oferece só os papéis que cabem no ambiente de cada pessoa, mas quem // ux-neutrality:allow doc-comment
 * decide é o servidor: toda recusa aparece com a frase da razão exata, e é ela // ux-neutrality:allow doc-comment
 * que o teste compara. // ux-neutrality:allow doc-comment
 */
export function AdminUsuariosPage() {
  const apiClient = useApiClient();
  const [usuarios, setUsuarios] = useState<UsuarioAdmin[] | null>(null);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(0);
  const [escritorios, setEscritorios] = useState<EscritorioAdmin[]>([]);
  const [clientes, setClientes] = useState<ClienteAdmin[]>([]);
  const [erroLista, setErroLista] = useState<string | null>(null);
  const [erroAcao, setErroAcao] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [busca, setBusca] = useState('');
  const [ambiente, setAmbiente] = useState<AmbienteAdmin | ''>('');
  const [estado, setEstado] = useState<EstadoAdmin>('ativos');
  // E175.F1: a criação e a edição vivem em janelas; a lista fica só com a busca.
  const [criando, setCriando] = useState(false);
  const [editando, setEditando] = useState<string | null>(null);
  // Só a carga mais recente escreve no estado — a resposta lenta de um filtro
  // anterior pintaria uma lista que já não é a pedida (F-E51.F4-02).
  const pedidoRef = useRef(0);

  const carregar = useCallback(async () => {
    const pedido = pedidoRef.current + 1;
    pedidoRef.current = pedido;
    const termo = busca.trim();
    try {
      const resposta = await listUsuarios(apiClient, {
        ...(termo === '' ? {} : { busca: termo }),
        ...(ambiente === '' ? {} : { ambiente }),
        estado,
        limite: _POR_PAGINA,
        offset: pagina * _POR_PAGINA,
      });
      if (pedido !== pedidoRef.current) return;
      setUsuarios(resposta.itens);
      setTotal(resposta.total);
      setErroLista(null);
    } catch (err) {
      if (pedido !== pedidoRef.current) return;
      setUsuarios([]);
      setErroLista(_mensagem(err));
    }
  }, [apiClient, ambiente, busca, estado, pagina]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  useEffect(() => {
    let ativo = true;
    void (async () => {
      try {
        const [pagina_, lista] = await Promise.all([
          listEscritorios(apiClient, { estado: 'ativos', limite: 100 }),
          listClientes(apiClient),
        ]);
        if (!ativo) return;
        setEscritorios(pagina_.itens);
        setClientes(lista);
      } catch {
        // O formulário de criação fica sem opções; a lista principal segue —
        // e o erro da ação, quando houver, é o que explica.
      }
    })();
    return () => {
      ativo = false;
    };
  }, [apiClient]);

  /**
   * Executa uma ação administrativa e **responde se ela aconteceu**. // ux-neutrality:allow doc-comment
   *
   * O retorno existe para quem tem estado a descartar: engolir a recusa e // ux-neutrality:allow doc-comment
   * devolver sucesso fazia o formulário limpar a senha provisória depois de um // ux-neutrality:allow doc-comment
   * e-mail duplicado — dado que a tela não sabe recuperar (F-E53.F6-01). // ux-neutrality:allow doc-comment
   */
  async function executar(acao: () => Promise<void>, mensagemSucesso: string): Promise<boolean> {
    setSalvando(true);
    setErroAcao(null);
    setSucesso(null);
    try {
      await acao();
      setSucesso(mensagemSucesso);
      await carregar();
      return true;
    } catch (err) {
      setErroAcao(_mensagem(err));
      return false;
    } finally {
      setSalvando(false);
    }
  }

  const inicio = total === 0 ? 0 : pagina * _POR_PAGINA + 1;
  const fim = Math.min(total, (pagina + 1) * _POR_PAGINA);

  return (
    <section className="mx-auto flex w-full max-w-5xl flex-col gap-5 p-6">
      <header>
        <p className="mb-1.5 font-titulo text-rotulo uppercase text-fg-dim">
          {ptBR.admin.usuariosEyebrow}
        </p>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-display-md text-fg">{ptBR.admin.usuariosTitle}</h1>
          <Button type="button" onClick={() => setCriando(true)} data-testid="nova-pessoa-abrir">
            {ptBR.admin.novaPessoaAbrir}
          </Button>
        </div>
        <p className="mt-1 text-body-md text-fg-muted">{ptBR.admin.usuariosIntro}</p>
      </header>

      {sucesso !== null ? (
        <p role="status" data-testid="admin-sucesso" className="text-body-sm text-accent-700">
          {sucesso}
        </p>
      ) : null}
      {erroAcao !== null ? (
        <p role="alert" data-testid="admin-erro-acao" className="text-body-sm text-danger-700">
          {erroAcao}
        </p>
      ) : null}

      {criando ? (
        <Dialog open onOpenChange={(aberto) => !aberto && !salvando && setCriando(false)}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogTitle className="sr-only">{ptBR.admin.novaPessoaTitle}</DialogTitle>
            <NovaPessoaForm
              escritorios={escritorios}
              clientes={clientes}
              salvando={salvando}
              onCriar={async (payload) => {
                const criada = await executar(async () => {
                  await criarPessoa(apiClient, payload);
                }, ptBR.admin.novaPessoaSucesso);
                if (criada) setCriando(false);
                return criada;
              }}
            />
            {erroAcao !== null ? (
              <p role="alert" className="px-sc-3 text-body-sm text-danger-700">
                {erroAcao}
              </p>
            ) : null}
          </DialogContent>
        </Dialog>
      ) : null}
      {editando !== null ? (
        <PessoaDialog
          usuarioId={editando}
          escritorios={escritorios}
          onFechar={() => setEditando(null)}
          onAlterada={() => void carregar()}
        />
      ) : null}

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex min-w-[220px] flex-col gap-1 text-body-sm text-fg-muted">
          <span>{ptBR.admin.buscaLabel}</span>
          <Input
            value={busca}
            onChange={(evento) => {
              setBusca(evento.target.value);
              setPagina(0);
            }}
            data-testid="usuarios-busca"
          />
        </label>
        <label className="flex flex-col gap-1 text-body-sm text-fg-muted">
          <span>{ptBR.admin.ambienteFiltroLabel}</span>
          <Select
            value={ambiente}
            onChange={(evento) => {
              setAmbiente(evento.target.value as AmbienteAdmin | '');
              setPagina(0);
            }}
            data-testid="usuarios-ambiente"
          >
            <option value="">{ptBR.admin.ambienteTodos}</option>
            <option value="plataforma">{ptBR.admin.ambientePlataforma}</option>
            <option value="escritorio">{ptBR.admin.ambienteEscritorio}</option>
            <option value="banco">{ptBR.admin.ambienteBanco}</option>
          </Select>
        </label>
        <label className="flex flex-col gap-1 text-body-sm text-fg-muted">
          <span>{ptBR.admin.estadoLabel}</span>
          <Select
            value={estado}
            onChange={(evento) => {
              setEstado(evento.target.value as EstadoAdmin);
              setPagina(0);
            }}
            data-testid="usuarios-estado"
          >
            <option value="ativos">{ptBR.admin.estadoAtivos}</option>
            <option value="inativos">{ptBR.admin.estadoInativos}</option>
            <option value="todos">{ptBR.admin.estadoTodos}</option>
          </Select>
        </label>
      </div>

      <UsuariosTable usuarios={usuarios} erro={erroLista} onEditar={setEditando} />

      <div className="flex items-center gap-3 text-body-sm text-fg-muted">
        <Button
          variant="secondary"
          type="button"
          disabled={pagina === 0}
          onClick={() => setPagina((atual) => Math.max(0, atual - 1))}
          data-testid="usuarios-anterior"
        >
          {ptBR.admin.paginaAnterior}
        </Button>
        <Button
          variant="secondary"
          type="button"
          disabled={fim >= total}
          onClick={() => setPagina((atual) => atual + 1)}
          data-testid="usuarios-proxima"
        >
          {ptBR.admin.paginaProxima}
        </Button>
        <span data-testid="usuarios-resumo">
          {ptBR.admin.paginaResumo
            .replace('{inicio}', String(inicio))
            .replace('{fim}', String(fim))
            .replace('{total}', String(total))}
        </span>
      </div>
    </section>
  );
}

export default AdminUsuariosPage;
